const extend = require('xtend/mutable');
const q = require('queried');
const Tip = require('component-tip');
const ajax = require('component-ajax');
const on = require('emmy/on');
const keys = require('key-name');
const capitalize = require('capitalize');
const _ = require('./i18n');
const {
  getIcon,
} = require('./utils');

/**
 * Search controller class
 * @param {DOM element} form
 * @param {Object} options - init options
 * @constructor
 */
function Search(form, options) {
  const defaults = {
    maxResults: 5,
    threshold: 2, // minimal number of symbols to make request
    updateDelay: 300,
    popup: q('.search-suggestions', form),

    // these selectors will be queried inside this.popup
    resultsLink: '.all-results-link',
    resultsLinkText: '.all-results-link-text',
    list: '.suggestion-list',
  };

  extend(this, defaults, options);

  this.form = form;
  this.queryInput = q('[name="q"]', form);
  this.locationInput = q('[name="location"]', form);

  this.queryInput.setAttribute('aria-haspopup', 'true');

  // change popup selectors to DOM elements if they're not yet
  this.popup = q(this.popup);
  this.resultsLink = q(this.resultsLink, this.popup);
  this.resultsLinkText = q(this.resultsLinkText, this.popup);
  this.list = q(this.list, this.popup);

  this.dropdown = new Tip(this.popup);
  this.dropdown.classname = 'search-suggestions-dropdown';
  this.dropdown.position('bottom', {
    auto: false,
  });

  this.list.setAttribute('role', 'menu');

  // bind events
  this.bindEvents();

  this.popup.removeAttribute('hidden');
  this.hidePopup();
}

extend(Search.prototype, {

  /**
   * show the search preview
   */
  showPopup() {
    this.load(this.queryInput.value);
    this.dropdown.show(this.queryInput);
  },

  /**
   * hide the search preview
   */
  hidePopup() {
    this.dropdown.hide();
  },

  /**
   * bind the controller's events listeners
   */
  bindEvents() {
    on(this.popup, 'click', (e) => {
      e.stopPropagation();
    });

    // input events
    on(this.queryInput, 'input', () => {
      if (this.queryInput.value.length < this.threshold) {
        this.hidePopup();
      } else {
        this.showPopup();
      }

      this.updateResultsLink();
      this.load(this.queryInput.value);
    });

    on(this.queryInput, 'click', (e) => {
      if (this.queryInput.value.length >= this.threshold) {
        e.stopPropagation();
        this.showPopup();
      }
    });

    on(this.queryInput, 'keydown', (e) => {
      if (e.which === keys.tab || e.which === keys.down) {
        e.preventDefault();
        const nextEl = this.list.children[0];
        if (nextEl) {
          nextEl.focus();
        }
      }
    });

    on(this.queryInput, 'keyup', (e) => {
      if (e.which === keys.escape) this.hidePopup();
    });

    on(this.resultsLink, 'keydown', (e) => {
      if (e.which === keys.up) {
        e.preventDefault();
        if (this.list.lastChild) {
          this.list.lastChild.focus();
        }
      } else if (e.which === keys.down) {
        e.preventDefault();
      }
    });

    // document events
    on(document, 'click', () => {
      this.hidePopup();
    });
  },

  /**
   * set the updated number and link on results link
   */
  updateResultsLink() {
    const params = {
      q: this.queryInput.value,
      location: this.locationInput.value,
    };
    const url = `/search/?q=${params.q}&location=${params.location}`;
    this.resultsLink.setAttribute('href', url);
    this.resultsLinkText.textContent = _.interpolate(_('Search for “%s”'), [this.queryInput.value]);
  },

  /**
   * update popup info
   * @param  {Object} data
   */
  updatePopup(data) {
    this.list.classList.remove('preloading');

    if (!data || data.error) {
      this.renderSuggestionsList();
    } else {
      this.renderSuggestionsList(data && data.results || []);
    }

    this.bindKeyboardNavigation();
  },

  /**
   * make request to get results and update state
   */
  load() {
    const self = this;
    const params = {
      q: self.queryInput.value,
      location: this.locationInput.value,
    };

    // prevent it not enough letters
    if (self.queryInput.value.length < this.threshold) return;

    self.list.classList.add('preloading');

    // timeout to restrict number of requests to server
    clearTimeout(self.requestTimeout);
    self.requestTimeout = setTimeout(() => {
      ajax({
        url: '/quick-search/',
        data: params,
        dataType: 'json',
        contentType: 'application/json',
        success: self.updatePopup.bind(self),
        error: self.updatePopup.bind(self),
      });
    }, self.updateDelay);
  },

  /**
   * bind suggestions items for keyboard actions
   */
  bindKeyboardNavigation() {
    const suggestions = q.all('.suggestion', this.list);

    suggestions.forEach((suggestion) => {
      on(suggestion, 'keydown', (e) => {
        e.preventDefault();

        if (e.which === keys.enter) {
          this.handleEnterKeydown(suggestion);
        } if (e.which === keys.down) {
          e.preventDefault();
          if (suggestion.nextSibling) {
            suggestion.nextSibling.focus();
          } else {
            this.resultsLink.focus();
          }
        } if (e.which === keys.up) {
          e.preventDefault();
          if (suggestion.previousSibling) {
            suggestion.previousSibling.focus();
          } else {
            this.queryInput.focus();
          }
        }
      });
    });
  },

  handleEnterKeydown(suggestion) {
    const link = q('a', suggestion);
    if (link) window.location = link.href;
  },

  /**
   * render the whole suggestions list
   * @param  {Object} data - data from server
   */
  renderSuggestionsList(data) {
    let str = '';
    if (!data || data.error) {
      const message = _('We hoped you won\'t read this');
      str = `<li class="suggestion-notification">${message}</li>`;
    } else if (data.length) {
      const end = Math.min(data.length, this.maxResults);
      data[0].first = true;
      for (let i = 0; i < end; i++) {
        str += this.renderSuggestion(data[i]);
      }
    } else {
      const nothing = _('Nothing found for this query');
      str = `<li class="suggestion-notification">${nothing}</li>`;
    }
    this.list.innerHTML = str;
  },

  /**
   * render suggestions item
   * @param  {Object} data - data from server (updated with calling func)
   * @return {String} - ready HTML-markup of the rendered item
   */
  renderSuggestion(data) {
    let str = '';
    str += `<li class="suggestion ${data.ctype}" role="menuitem" aria-controls="search" tabindex="1">`;
    str += '<div class="suggestion-description">';
    str += `<h3><a href="${data.url}">${data.title[0].toUpperCase()}${data.title.slice(1)}</a></h3>`;
    if (data.ctype === 'place' && data.address && !data.address_is_hidden) {
      str += `<address class="suggestion-detail">${getIcon('location')}&nbsp;${data.address}</address>`;
    } else if (data.ctype === 'event') {
      let timeStr;

      if (data.date_is_yet_unknown) {
        timeStr = _('Date is yet unknown');
      } else if (data.is_permanent) {
        timeStr = data.place_working_hours || _('Always');
      } else {
        timeStr = data.display_dates;
      }

      str
        += `<time class="suggestion-detail">${getIcon('calendar')}&nbsp;${
          capitalize(timeStr)
        }</time>`;
    }
    str += '</div>';
    str += '</li>';
    return str;
  },

});

module.exports = Search;
